import type { TFunction } from 'i18next'
import { useEffect, useRef } from 'react'
import { StyleSheet } from 'react-native'
import { WebView, type WebViewMessageEvent } from 'react-native-webview'

import { logger } from '../../logging/logger'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import { coinbaseFailureCopy } from './coinbaseFailureCopy'
import { readOnrampEvent } from './coinbaseOnrampEvents'

/** Their page has never taken this long to draw a button. Load only. */
const LOAD_TIMEOUT_MS = 60_000

export interface CoinbaseCheckoutSurfaceProps {
  checkoutUrl: string
  /** Log context only — the contract says URL surfaces may carry none. */
  environment: string
  /** The button is drawn and can be pressed. */
  onReady: () => void
  /** The user paid; the transfer is theirs to run from here. */
  onPaid: () => void
  /** Their polling saw the crypto sent. */
  onSucceeded: () => void
  onFailed: (code: string, detail?: string) => void
}

/**
 * Coinbase's hosted Apple Pay button.
 *
 * Their page draws nothing but the button and waits for a real tap on it —
 * pressing it from injected script reaches `pending_payment_auth` and stops,
 * the sheet never opens — so the WebView is sized as the button and sat in
 * the action's own slot (design 2114:8131: "Deposit with Apple Pay" is the
 * amount screen's action, not a page of its own).
 *
 * - a cancelled sheet leaves the page loaded and the button live, so pressing
 *   it again opens the sheet rather than re-running the order
 * - the page paints no background of its own once told to, so the frame
 *   around it decides the button's shape
 */
export function CoinbaseCheckoutSurface({
  checkoutUrl,
  environment,
  onReady,
  onPaid,
  onSucceeded,
  onFailed,
}: CoinbaseCheckoutSurfaceProps) {
  const { t } = useFunkitTranslation()

  // A page that never reports itself ready has no button to show, and the
  // slot would wait on a tap that is not coming. It covers the load and
  // nothing after it: once the button is there the wait is the user's, and
  // reading the page or retrying a dismissed sheet outlasts any load.
  const fail = useRef(onFailed)
  fail.current = onFailed
  const loadTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => {
    loadTimer.current = setTimeout(() => {
      logger.warn('connect-rn:fiat:coinbase_checkout_timed_out', {
        environment,
      })
      fail.current('load_timeout')
    }, LOAD_TIMEOUT_MS)
    return () => clearTimeout(loadTimer.current)
  }, [environment])

  const onMessage = ({ nativeEvent }: WebViewMessageEvent) => {
    const event = readOnrampEvent(nativeEvent.data)
    if (event === undefined) {
      return
    }
    if (event.kind === 'READY') {
      logger.info('connect-rn:fiat:coinbase_checkout_ready', { environment })
      clearTimeout(loadTimer.current)
      onReady()
      return
    }
    if (event.kind === 'PAID') {
      logger.info('connect-rn:fiat:coinbase_checkout_paid', { environment })
      onPaid()
      return
    }
    if (event.kind === 'SUCCEEDED') {
      logger.info('connect-rn:fiat:coinbase_checkout_succeeded', {
        environment,
      })
      onSucceeded()
      return
    }
    if (event.kind === 'CANCELLED') {
      logger.info('connect-rn:fiat:coinbase_checkout_cancelled', {
        environment,
      })
      return
    }
    if (event.kind === 'FAILED') {
      logger.warn('connect-rn:fiat:coinbase_checkout_failed', {
        environment,
        code: event.code,
        // Logged, never shown — see `failureDetail`.
        providerMessage: event.message,
      })
      onFailed(event.code, coinbaseFailureCopy(event.code, t))
      return
    }
    logger.debug('connect-rn:fiat:coinbase_checkout_progress', {
      environment,
      event: event.name,
    })
  }

  return (
    <WebView
      source={{ uri: checkoutUrl }}
      onMessage={onMessage}
      onError={() => onFailed('load_failed')}
      onLoadStart={() => {
        logger.info('connect-rn:fiat:coinbase_checkout_load_start', {
          environment,
        })
      }}
      onLoadEnd={() => {
        logger.info('connect-rn:fiat:coinbase_checkout_load_end', {
          environment,
        })
      }}
      onHttpError={({ nativeEvent }) => {
        logger.warn('connect-rn:fiat:coinbase_checkout_http_error', {
          environment,
          status: nativeEvent.statusCode,
        })
      }}
      // Every one of these is Coinbase's own React Native reference
      // (`APIGuestCheckoutWidget`). `paymentRequestEnabled` is what lets
      // the page raise a payment sheet at all, and the open whitelist is
      // what stops a scheme their sheet navigates through being blocked.
      javaScriptEnabled
      domStorageEnabled
      sharedCookiesEnabled
      thirdPartyCookiesEnabled
      allowsInlineMediaPlayback
      javaScriptCanOpenWindowsAutomatically
      paymentRequestEnabled
      setSupportMultipleWindows={false}
      startInLoadingState={false}
      mixedContentMode="compatibility"
      originWhitelist={ORIGINS}
      // The page is the button and nothing around it: no scrolling, no
      // bounce, and no paint of its own behind the button, so the frame's fill
      // is what shows through.
      scrollEnabled={false}
      bounces={false}
      injectedJavaScript={STYLE_APPLE_PAY_BUTTON}
      style={styles.page}
    />
  )
}

// Open, as Coinbase's reference has it: their sheet navigates through
// schemes of its own, and a blocked one is a sheet that never opens. Only
// their `https` link is ever loaded here, and the adapter checks that.
const ORIGINS = ['*']

// Style the provider's existing control so its trusted tap handler stays intact.
// Observe late mounts and provider re-renders without changing payment state.
const STYLE_APPLE_PAY_BUTTON = `
  (function () {
    var style = document.createElement('style');
    style.textContent =
      'html,body{background:transparent!important;margin:0!important;overflow:hidden!important}' +
      '.onramp-apple-pay-button{width:100%!important;max-width:none!important}' +
      'div:has(>.onramp-apple-pay-button){padding:0!important}' +
      'apple-pay-button{' +
        '--apple-pay-button-width:100%;' +
        '--apple-pay-button-height:56px;' +
        '--apple-pay-button-border-radius:28px;' +
        '--apple-pay-button-padding:10px 0px;' +
        '--apple-pay-button-box-sizing:border-box;' +
      '}';
    document.head.appendChild(style);
    function styleButton() {
      var button = document.querySelector('apple-pay-button');
      if (!button) return;
      if (button.getAttribute('buttonstyle') !== 'white') {
        button.setAttribute('buttonstyle', 'white');
      }
      if (button.getAttribute('type') !== 'pay') {
        button.setAttribute('type', 'pay');
      }
    }
    new MutationObserver(styleButton).observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['buttonstyle', 'type']
    });
    styleButton();
  })();
  true;
`

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: 'transparent' },
})
